import {type JSX, useEffect, type ComponentProps} from 'react';
import {
  type PressReleasePreviewEditorVersionEnum,
  ReleaseDeliveryStatus,
} from '@openapi/press-release';
import {clsx} from 'clsx';
import {
  LogoIcon,
  DesktopIcon,
  MailIcon,
  MobileIcon,
  SocialIcon,
  WebIcon,
} from '../icons';
import {
  useGetPreviewMediaType,
  useSetPreviewMediaType,
} from '../../context/preview-media-type';
import {getParameters} from '../../lib/parameters';
import {Pulldown} from '../pulldown';
import {type PreviewDeviceType} from '../../hooks/use-preview-device-state';
import styles from './preview-control-header.module.css';
import {EXPERIMENTS} from '@/constants';

const PREVIEW_SHAREABLE_STATUSES = new Set<ReleaseDeliveryStatus>([
  ReleaseDeliveryStatus.Draft,
  ReleaseDeliveryStatus.Scheduled,
]);

const EXPERIMENT_PREVIEW_SHAREABLE_STATUSES = new Set<ReleaseDeliveryStatus>([
  ReleaseDeliveryStatus.Unpublished,
  ReleaseDeliveryStatus.RepublishReserved,
  ReleaseDeliveryStatus.PublishedMediaListOnly,
  ReleaseDeliveryStatus.WithdrawnByReview,
]);

const previewMediaTypeOptions = [
  {key: 'PR TIMES Web', value: 'web', Icon: WebIcon},
  {key: 'ソーシャル', value: 'social', Icon: SocialIcon},
  {key: 'メール', value: 'mail', Icon: MailIcon},
] satisfies ComponentProps<typeof Pulldown>['options'];

const deviceOptions = [
  {key: 'デスクトップ', value: 'desktop', Icon: DesktopIcon},
  {key: 'モバイル', value: 'mobile', Icon: MobileIcon},
] satisfies ComponentProps<typeof Pulldown>['options'];

type Props = {
  readonly isNotYetDelivered: boolean;
  readonly deliveryStatus: ReleaseDeliveryStatus;
  readonly editorVersion: PressReleasePreviewEditorVersionEnum | null;
  readonly renderTestMailButton: () => JSX.Element;
  readonly renderSharePreviewButton: () => JSX.Element;
  readonly device: PreviewDeviceType;
  readonly setDevice: (device: PreviewDeviceType) => void;
};

export function PreviewControlHeader({
  isNotYetDelivered,
  deliveryStatus,
  editorVersion,
  renderTestMailButton,
  renderSharePreviewButton,
  device,
  setDevice,
}: Props) {
  const previewMediaType = useGetPreviewMediaType();
  const setPreviewMediaType = useSetPreviewMediaType();

  const {page: previewPageType} = getParameters();

  // テストメール配信ボタンはv3エディターかつプレビューページかつ未配信の場合のみ表示
  // TODO: v2廃止後にv2か否かの判定処理を削除する
  const isOldRelease = editorVersion === null;
  const showTestMailButton =
    !isOldRelease && previewPageType === 'preview' && isNotYetDelivered;
  // ソーシャルプレビュー表示時、デバイスプルダウンを非表示
  const showDevicePulldown = previewMediaType !== 'social';
  // 共有・終了ボタンはプレビューページのみ表示
  const showAdminMenu = previewPageType === 'preview';
  const showSharePreviewButton =
    PREVIEW_SHAREABLE_STATUSES.has(deliveryStatus) ||
    (EXPERIMENTS.use_request_stop_and_republish_press_release &&
      EXPERIMENT_PREVIEW_SHAREABLE_STATUSES.has(deliveryStatus));
  // V3以前の場合メールプレビューを表示する
  useEffect(() => {
    if (isOldRelease) previewMediaTypeOptions.pop();
  }, [isOldRelease]);

  const onClickExit = () => {
    window.close();
  };

  return (
    <header className={styles.header}>
      <div className={styles.left}>
        <LogoIcon />
        <p className={styles.pageTitle}>プレビュー</p>
      </div>

      <div className={styles.middle}>
        <Pulldown
          ariaLabel='プレビューするページの種類'
          id='preview_media_type'
          options={previewMediaTypeOptions}
          value={previewMediaType}
          align='end'
          style={{width: 148}}
          onValueChange={setPreviewMediaType}
        />
        {showDevicePulldown && (
          <Pulldown
            ariaLabel='プレビューのデバイス'
            id='device'
            options={deviceOptions}
            value={device}
            align='start'
            style={{width: 122}}
            onValueChange={setDevice}
          />
        )}
        {showTestMailButton && renderTestMailButton()}
      </div>

      <div className={styles.right}>
        {showAdminMenu && (
          <>
            {showSharePreviewButton && renderSharePreviewButton()}
            <button
              type='button'
              className={clsx(styles.button, styles.buttonFlat)}
              onClick={onClickExit}
            >
              終了
            </button>
          </>
        )}
      </div>
    </header>
  );
}
